import { useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { range } from 'lodash';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import z from 'zod';
import { dayjs } from '@lgg/isomorphic/dayjs';
import { getDatesDifferenceInUnits } from '@lgg/isomorphic/i18n/date-utils';
import { LggBaseDropdown } from 'src/components/general/button/dropdown-button';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { LggDateTimePicker } from 'src/components/general/inputs/custom-date-pickers/lgg-date-time-picker';
import { LggToggleSwitch } from 'src/components/general/inputs/lgg-toggle-switch';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useVisible } from 'src/hooks/use-visible';

const InputsRow = styled(FlexRow)`
  flex: 1;
  gap: 10px;

  & > * {
    flex: 1;
  }
`;

const AllDayLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 18px;
`;

const appointmentDatePickerFormSchema = z.object({
  startDate: z.date(),
  endDate: z.date(),
  isAllDay: z.boolean(),
  startTime: z.number(),
  endTime: z.number(),
});

type AppointmentDateTimePickerProps = {
  onApply: ({
    startDate,
    endDate,
    isAllDay,
  }: {
    startDate: Date;
    endDate?: Date;
    isAllDay: boolean;
  }) => void;
  startDate?: Date;
  endDate?: Date;
  isAllDay?: boolean;
  children: React.ReactNode;
};

const DesktopHeader = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
  padding: 15px;
`;

const FooterActionsContainer = styled(FlexRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  gap: 10px;
  justify-content: flex-end;
  padding: 10px;

  & > * {
    width: max-content;
    max-height: 30px;
  }
`;

const DateAndTimeLabel = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  margin-right: 10px;
  width: 60px;
`;

const OverlayContent = styled(FlexColumn)`
  gap: 10px;
  padding: 15px;
`;

const StyledSelect = styled(Select)`
  height: 34px;
` as unknown as typeof Select;

const AllDaySwitchContainer = styled(FlexRow)`
  gap: 10px;
  align-items: center;
  margin-bottom: 5px;
`;

const InputFooterActions = ({
  onSave,
  onCancel,
}: {
  onSave: VoidFunction;
  onCancel: VoidFunction;
}) => {
  const { t } = useTranslation(['common']);

  return (
    <FooterActionsContainer>
      <ButtonV2
        size="small"
        variant="default"
        data-lgg-id="appointment-date-time-picker-cancel-button"
        onClick={onCancel}
      >
        {t('common:cancel')}
      </ButtonV2>
      <ButtonV2
        size="small"
        variant="primary"
        data-lgg-id="appointment-date-time-picker-apply-button"
        onClick={onSave}
      >
        {t('common:apply')}
      </ButtonV2>
    </FooterActionsContainer>
  );
};

const OPTION_MINUTES_INTERVAL = 15;

export const AppointmentDateTimePicker = ({
  onApply,
  startDate,
  endDate,
  isAllDay,
  children,
}: AppointmentDateTimePickerProps) => {
  const { t } = useTranslation(['common', 'appointments']);
  const { format, addMinutes, isBefore, isEqual, startOfDay, startOfToday } =
    useDateHelpers();
  const breakpointUpMd = useBreakpoint(up('md'));
  const visibilityHandler = useVisible();

  const getTimeOptionFromDate = useCallback(
    (date: Date) => {
      const { minutes } = getDatesDifferenceInUnits(startOfDay(date), date);

      return Math.max(0, Math.min(95, Math.round(minutes / OPTION_MINUTES_INTERVAL)));
    },
    [startOfDay],
  );

  const formValues = useMemo(
    () => ({
      startDate: startDate ?? startOfToday(),
      endDate: endDate ?? startOfToday(),
      startTime: startDate ? getTimeOptionFromDate(startDate) : 0,
      endTime: endDate ? getTimeOptionFromDate(endDate) : 1,
      isAllDay: isAllDay ?? false,
    }),
    [startDate, endDate, isAllDay, getTimeOptionFromDate, startOfToday],
  );

  const form = useForm<z.infer<typeof appointmentDatePickerFormSchema>>({
    resolver: zodResolver(appointmentDatePickerFormSchema),
    defaultValues: formValues,
    values: formValues,
  });

  const onSubmit = useCallback(
    (values: z.infer<typeof appointmentDatePickerFormSchema>) => {
      const { startDate, endDate, startTime, endTime, isAllDay } = values;
      const endDateWithTime = endDate
        ? addMinutes(startOfDay(endDate), endTime * OPTION_MINUTES_INTERVAL)
        : undefined;
      const startDateWithTime = addMinutes(
        startOfDay(startDate),
        startTime * OPTION_MINUTES_INTERVAL,
      );

      if (endDateWithTime) {
        if (
          (!isAllDay && isBefore(endDateWithTime, startDateWithTime)) ||
          (isAllDay && isBefore(endDate, startDate))
        ) {
          form.setError('endDate', {
            message: t(
              'appointments:dateAndTimePicker.inputs.endDate.errors.endDateBeforeStartDate',
            ),
          });
          return;
        }
      }

      onApply({
        startDate: addMinutes(startOfDay(startDate), startTime * OPTION_MINUTES_INTERVAL),
        endDate: endDate
          ? addMinutes(startOfDay(endDate), endTime * OPTION_MINUTES_INTERVAL)
          : undefined,
        isAllDay,
      });

      visibilityHandler.close();
    },
    [addMinutes, form, isBefore, onApply, startOfDay, t, visibilityHandler],
  );

  const handleApply = useCallback(() => {
    void form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleCancel = useCallback(() => {
    visibilityHandler.close();
    form.reset();
  }, [form, visibilityHandler]);

  const watchedStartDate = form.watch('startDate');
  const watchedEndDate = form.watch('endDate');
  const watchedStartTime = form.watch('startTime');
  const watchedIsAllDay = form.watch('isAllDay');

  const startDateWithTime = useMemo(() => {
    return addMinutes(
      startOfDay(watchedStartDate),
      watchedStartTime * OPTION_MINUTES_INTERVAL,
    );
  }, [addMinutes, startOfDay, watchedStartDate, watchedStartTime]);

  const getTimeOptionLabel = useCallback(
    (date: Date) => {
      return format(date, 'h:mm A');
    },
    [format],
  );

  const startDateOptions = useMemo(() => {
    const now = dayjs().toDate();
    const nowMinutes = getDatesDifferenceInUnits(startOfDay(now), now).minutes;
    const nowFlooredMinutes =
      Math.floor(nowMinutes / OPTION_MINUTES_INTERVAL) * OPTION_MINUTES_INTERVAL;
    const nowFloored = addMinutes(startOfDay(now), nowFlooredMinutes);

    const timeOptions = range(0, 96)
      .map((index) => {
        const totalMinutes = index * OPTION_MINUTES_INTERVAL;
        const referenceDate = addMinutes(startOfDay(watchedStartDate), totalMinutes);
        const formattedTime = getTimeOptionLabel(referenceDate);

        if (isBefore(referenceDate, nowFloored)) {
          return null;
        }

        return {
          value: index,
          label: formattedTime,
        };
      })
      .filter(Boolean) as SelectOption<number>[];

    return timeOptions;
  }, [addMinutes, getTimeOptionLabel, isBefore, startOfDay, watchedStartDate]);

  const getDurationPreview = useCallback(
    (durationInMinutes: number) => {
      // If duration is greater than 24 hours, return empty string
      if (durationInMinutes > 1440) {
        return '';
      }

      const value =
        durationInMinutes >= 60
          ? t('common:timeUnits.hour_short_short', {
              value: +(durationInMinutes / 60).toFixed(2),
            })
          : t('common:timeUnits.minute_short_short', {
              value: +durationInMinutes.toFixed(2),
            });

      return `(${value})`;
    },
    [t],
  );

  const endTimeOptions = useMemo(() => {
    const timeOptions = range(0, 96)
      .map((index) => {
        const totalMinutes = index * OPTION_MINUTES_INTERVAL;
        const endDateWithTime = addMinutes(startOfDay(watchedEndDate), totalMinutes);
        const formattedTime = getTimeOptionLabel(endDateWithTime);
        const durationInMinutes = getDatesDifferenceInUnits(
          startDateWithTime,
          endDateWithTime,
        ).minutes;

        const isInvalid =
          isBefore(endDateWithTime, startDateWithTime) ||
          isEqual(endDateWithTime, startDateWithTime);

        if (isInvalid) {
          return null;
        }

        return {
          value: index,
          label: `${formattedTime} ${getDurationPreview(durationInMinutes)}`,
        };
      })
      .filter(Boolean) as SelectOption<number>[];

    return timeOptions;
  }, [
    addMinutes,
    getTimeOptionLabel,
    getDurationPreview,
    isBefore,
    isEqual,
    startDateWithTime,
    startOfDay,
    watchedEndDate,
  ]);

  const content = useMemo(() => {
    return (
      <OverlayContent>
        <FlexRow>
          <Controller
            control={form.control}
            name="isAllDay"
            render={({ field }) => (
              <AllDaySwitchContainer>
                <LggToggleSwitch
                  data-lgg-id="appointment-date-time-picker-all-day-switch"
                  checked={field.value}
                  onChange={field.onChange}
                />
                <AllDayLabel>
                  {t('appointments:dateAndTimePicker.inputs.allDaySwitch.label')}
                </AllDayLabel>
              </AllDaySwitchContainer>
            )}
          />
        </FlexRow>
        <FlexRow>
          <DateAndTimeLabel>
            {t('appointments:dateAndTimePicker.inputs.startDate.label')}
          </DateAndTimeLabel>
          <InputsRow>
            <Controller
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <LggDateTimePicker
                  testId="appointment-date-time-picker-start-date"
                  showPresetOptions
                  onApply={(date) => {
                    field.onChange(date);

                    if (date && isBefore(watchedEndDate, date)) {
                      form.setValue('endDate', date);
                    }
                  }}
                  inputValue={field.value}
                  inputDateFormat="MMM D, YYYY"
                  disabledDate={(date) => isBefore(date.toDate(), startOfToday())}
                />
              )}
            />

            <Controller
              control={form.control}
              name="startTime"
              render={({ field }) => (
                <StyledSelect<SelectOption<number>>
                  name="upsert-appointment-field-startTime"
                  options={startDateOptions}
                  onChange={(value) => field.onChange(value?.value)}
                  value={
                    (watchedIsAllDay
                      ? null
                      : startDateOptions.find(
                          (option) => option.value === field.value,
                        )) ?? null
                  }
                  placeholder={t(
                    'appointments:dateAndTimePicker.inputs.startDate.datePlaceholder',
                  )}
                  isDisabled={watchedIsAllDay}
                />
              )}
            />
          </InputsRow>
        </FlexRow>
        <FlexRow>
          <DateAndTimeLabel>
            {t('appointments:dateAndTimePicker.inputs.endDate.label')}
          </DateAndTimeLabel>
          <InputsRow>
            <Controller
              control={form.control}
              name="endDate"
              render={({ field, fieldState }) => (
                <LggDateTimePicker
                  testId="appointment-date-time-picker-end-date"
                  showPresetOptions
                  onApply={field.onChange}
                  inputValue={field.value}
                  inputDateFormat="MMM D, YYYY"
                  disabledDate={(date) =>
                    isBefore(date.toDate(), startOfToday()) ||
                    isBefore(date.toDate(), startDateWithTime)
                  }
                  hasError={Boolean(fieldState.error?.message)}
                />
              )}
            />
            <Controller
              control={form.control}
              name="endTime"
              rules={{
                required: !watchedIsAllDay,
              }}
              render={({ field, fieldState }) => (
                <StyledSelect<SelectOption<number>>
                  name="upsert-appointment-field-endTime"
                  options={endTimeOptions}
                  onChange={(value) => field.onChange(value?.value)}
                  value={
                    (watchedIsAllDay
                      ? null
                      : endTimeOptions.find((option) => option.value === field.value)) ??
                    null
                  }
                  placeholder={t(
                    'appointments:dateAndTimePicker.inputs.endDate.datePlaceholder',
                  )}
                  hasError={Boolean(fieldState.error?.message)}
                  isDisabled={watchedIsAllDay}
                />
              )}
            />
          </InputsRow>
        </FlexRow>
      </OverlayContent>
    );
  }, [
    form,
    t,
    watchedIsAllDay,
    isBefore,
    watchedEndDate,
    startOfToday,
    startDateOptions,
    startDateWithTime,
    endTimeOptions,
  ]);

  return breakpointUpMd ? (
    <LggBaseDropdown
      overlay={
        <FlexColumn>
          <DesktopHeader>{t('appointments:dateAndTimePicker.title')}</DesktopHeader>
          {content}
          <InputFooterActions onSave={handleApply} onCancel={handleCancel} />
        </FlexColumn>
      }
      visibilityHandler={visibilityHandler}
    >
      <FlexColumn>{children}</FlexColumn>
    </LggBaseDropdown>
  ) : (
    <>
      <span onClick={visibilityHandler.show}>{children}</span>
      <BottomDrawer
        title={t('appointments:dateAndTimePicker.title')}
        visible={visibilityHandler.visible}
        onClose={visibilityHandler.close}
        footer={<InputFooterActions onSave={handleApply} onCancel={handleCancel} />}
      >
        {content}
      </BottomDrawer>
    </>
  );
};
